import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { SnackbarService } from '../../../common/services/snackbar.service'
import { notUndefined } from '../../../common/utils/conditional-assert'
import { TimesheetContainer } from '../timesheets.component'

@Component({
    selector: 'ppw-timesheets-table',
    templateUrl: './timesheets-table.component.html',
    styleUrls: ['./timesheets-table.component.scss']
})
export class TimesheetsTableComponent implements OnInit, AfterViewInit {
    @Input() readonly isViewByTag!: boolean
    @Input() readonly viewTimesheet!: boolean
    @Input() datasource$!: Observable<Array<TimesheetContainer>>
    @Input() readonly datasourceSubject!: BehaviorSubject<Array<TimesheetContainer>>
    @Input() readonly scrollToSelectedTimesheet$!: Observable<void>
    @Input() latestDatasource!: Array<TimesheetContainer>
    @Output() readonly timesheetClickedEmitter: EventEmitter<Array<TimesheetContainer>> = new EventEmitter<
        Array<TimesheetContainer>
    >()
    @Output() readonly timesheetDbClickedEmitter: EventEmitter<Array<TimesheetContainer>> = new EventEmitter<
        Array<TimesheetContainer>
    >()
    @Output() readonly latestDatasourceChangedEmitter: EventEmitter<Array<TimesheetContainer>> = new EventEmitter<
        Array<TimesheetContainer>
    >()

    @ViewChildren('timesheetRow', { read: ElementRef }) timesheetRows!: QueryList<ElementRef>
    @ViewChild('nameHeader') header?: ElementRef

    displayedColumns: Array<string> = ['name', 'sendInvoice', 'status', 'HTC', 'HNC', 'HTT', 'DTC', 'DNC', 'DTT']
    selectedTimesheets: Array<TimesheetContainer> = new Array<TimesheetContainer>()
    latestSelectedTimesheetIndex?: number

    constructor(private readonly snackbarService: SnackbarService) {}

    ngOnInit(): void {
        this.latestDatasource.forEach((t) => {
            this.select(t)
        })
    }

    ngAfterViewInit(): void {
        this.scrollToSelectedTimesheet$.subscribe(() => {
            this.scrollToSelectedTimesheet()
        })
    }

    select(timesheet: TimesheetContainer, event?: MouseEvent): Array<TimesheetContainer> {
        let result = new Array<TimesheetContainer>()
        if (timesheet.children === undefined) {
            if (event && (event.ctrlKey || event.metaKey)) {
                if (this.selectedTimesheets.includes(timesheet)) {
                    this.removeTimesheetFromSelection(timesheet)
                } else {
                    this.selectedTimesheets.push(timesheet)
                    // NOTE: we need a new reference here, or else the pipe won't trigger in the template
                    this.selectedTimesheets = [...this.selectedTimesheets]
                    this.latestSelectedTimesheetIndex = this.latestDatasource.findIndex((t) => t === timesheet)
                }
            } else if (this.selectedTimesheets.length > 0 && event && event.shiftKey) {
                // Tried to simulate the shift functionality in windows as much as possible.
                let currentIndex = 0
                const startIndex = notUndefined(this.latestSelectedTimesheetIndex)
                const endIndex = this.latestDatasource.findIndex((t) => t === timesheet)
                this.selectedTimesheets = []
                this.latestDatasource.forEach((row) => {
                    if (
                        !row.children &&
                        ((currentIndex >= startIndex && currentIndex <= endIndex) ||
                            (currentIndex <= startIndex && currentIndex >= endIndex))
                    ) {
                        this.selectedTimesheets.push(row)
                    }
                    currentIndex++
                })
            } else {
                this.selectedTimesheets = [timesheet]
                this.latestSelectedTimesheetIndex = this.latestDatasource.findIndex((t) => t === timesheet)
            }
        } else {
            timesheet.isExpanded = !timesheet.isExpanded
            if (!timesheet.isExpanded) {
                timesheet.children.forEach(() => {
                    const removedChildren = this.removeChildrenFromDataSource(timesheet)
                    const timesheetsToRemoveFromSelection = this.selectedTimesheets.filter((t) =>
                        removedChildren.includes(t)
                    )
                    timesheetsToRemoveFromSelection.forEach((t) => {
                        this.removeTimesheetFromSelection(t)
                    })
                })
            } else {
                const timesheetIndex = this.latestDatasource.findIndex((datasource) => datasource === timesheet)
                const childIndentLvl: number = timesheet.indentLvl ? timesheet.indentLvl + 1 : 1
                const childrenToAdd: Array<TimesheetContainer> = timesheet.children.map(
                    (child) =>
                        ({
                            ...child,
                            indentLvl: childIndentLvl,
                            parent: timesheet
                        } as unknown as TimesheetContainer)
                )
                this.latestDatasource.splice(timesheetIndex, 1, timesheet, ...childrenToAdd)
                result = childrenToAdd
            }
            this.datasourceSubject.next(this.latestDatasource)
            this.latestDatasourceChangedEmitter.next(this.latestDatasource)
        }
        this.timesheetClickedEmitter.emit(this.selectedTimesheets)

        return result
    }

    dbClickShowTimesheet(element: TimesheetContainer): void {
        if (!element.children) {
            this.timesheetDbClickedEmitter.emit()
        } else {
            if (element.indentLvl !== undefined) {
                const tempSelectedTimesheet = this.selectedTimesheets
                const tempLatestSelectedTimesheetIndex = this.latestSelectedTimesheetIndex
                this.recursivelySelectAllTimesheets(element)
                this.selectedTimesheets = tempSelectedTimesheet // Don't override the selectedTimesheets
                this.latestSelectedTimesheetIndex = tempLatestSelectedTimesheetIndex // Don't override the latestSelectedTimesheetIndex
                this.timesheetClickedEmitter.emit(this.selectedTimesheets)
            } else {
                this.snackbarService.promtSnackBar('Double-click is disabled for the root item (may take too long)')
            }
        }
    }

    private removeTimesheetFromSelection(timesheet: TimesheetContainer): void {
        const indexToDelete = this.selectedTimesheets.findIndex((t) => t === timesheet)
        this.selectedTimesheets.splice(indexToDelete, 1)
        // NOTE: we need a new reference here, or else the pipe won't trigger in the template
        this.selectedTimesheets = [...this.selectedTimesheets]
    }

    private removeChildrenFromDataSource(timesheet: TimesheetContainer): Array<TimesheetContainer> {
        const childrenToRemove = this.latestDatasource.filter((datasource) => datasource.parent === timesheet)
        this.latestDatasource = this.latestDatasource.filter((datasource) => !childrenToRemove.includes(datasource))
        if (timesheet.children) {
            childrenToRemove.forEach((child) => {
                childrenToRemove.push(...this.removeChildrenFromDataSource(child))
            })
        }

        return childrenToRemove
    }

    private recursivelySelectAllTimesheets(timesheetContainer: TimesheetContainer): void {
        if (timesheetContainer.isExpanded) {
            this.select(timesheetContainer) // the row was already expanded, close it and open it again.
        }
        const openedTimesheets = this.select(timesheetContainer)
        if (openedTimesheets && openedTimesheets.length > 0) {
            openedTimesheets.forEach((t) => {
                this.recursivelySelectAllTimesheets(t)
            })
        }
    }

    private scrollToSelectedTimesheet(): void {
        const selectedTimesheet = notUndefined(this.timesheetRows).find((sl: ElementRef) =>
            sl.nativeElement.classList.contains('latest-selected-timesheet')
        )
        const sideNavContent = document.querySelector('mat-sidenav-content')
        if (selectedTimesheet && sideNavContent && this.header) {
            const headerHeight = this.header.nativeElement.offsetHeight
            const node = selectedTimesheet.nativeElement
            sideNavContent.scrollTop = node.offsetTop - headerHeight // remove the header height here
        }
    }
}
